import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  UploadCloud,
  Inbox,
  ArrowRight,
  ClipboardCheck,
  LoaderCircle,
  FileText,
  Upload,
} from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useMe } from "@/hooks/useMe";
import { RESUME_ACCEPT, useResumeUpload } from "@/hooks/useResumeUpload";
import { displayNameFromEmail, type JobListItem } from "@/lib/jobs";
import { cn } from "@/lib/utils";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { StatCard } from "@/components/ui/stat-card";
import { ResumeTable } from "@/components/ResumeTable";
import { UploadStage } from "@/components/UploadStage";
import { PasteStage } from "@/components/PasteStage";

export function Dashboard() {
  const api = useApi();
  const me = useMe();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pasting, setPasting] = useState(false);
  const { handleFiles, pasteText, pending, setPending, confirmPending, cancelPending, uploading, error } =
    useResumeUpload();

  const jobsQuery = useQuery({
    queryKey: ["jobs"],
    queryFn: () => api.get<{ jobs: JobListItem[] }>("/api/resume-jobs"),
    refetchInterval: (query) =>
      query.state.data?.jobs.some((j) => j.status === "extracting") ? 2000 : false,
  });

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  // Confirmation lives in ResumeTable's dialog; this only runs after a yes.
  const deleteJob = useCallback(
    async (id: string) => {
      await api.delete(`/api/resume-jobs/${id}`);
      void jobsQuery.refetch();
    },
    [api, jobsQuery],
  );

  const jobs = jobsQuery.data?.jobs;
  const readyCount = jobs?.filter((j) => j.status === "ready_for_review").length ?? 0;
  const processingCount =
    jobs?.filter((j) => j.status === "extracting" || j.status === "created").length ?? 0;
  const failedCount = jobs?.filter((j) => j.status === "failed").length ?? 0;
  const documentCount = jobs?.reduce((sum, j) => sum + j.outputCount, 0) ?? 0;
  const firstName = displayNameFromEmail(me?.email);
  const recent = jobs?.slice(0, 6) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h2 className="mb-1">
          {firstName ? `Welcome back, ${firstName}.` : "Welcome back."}{" "}
          <span aria-hidden>👋</span>
        </h2>
        <p className="max-w-2xl text-[14px] text-text-2">
          Upload a candidate resume (PDF, DOCX, DOC, XLSX or TXT). The AI extracts it into a structured
          record you review before generating the AGP-branded document.
        </p>
      </header>

      <section className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          label="Ready for review"
          value={jobs ? readyCount : "–"}
          tone="ok"
          icon={<ClipboardCheck size={19} strokeWidth={1.75} aria-hidden />}
          caption="Extracted and waiting on you"
        />
        <StatCard
          label="Processing"
          value={jobs ? processingCount : "–"}
          tone="accent"
          valueToned={false}
          icon={
            <LoaderCircle
              size={19}
              strokeWidth={1.75}
              aria-hidden
              className={processingCount > 0 ? "animate-spin [animation-duration:2s]" : undefined}
            />
          }
          caption="Being read right now"
        />
        <StatCard
          label="Documents"
          value={jobs ? documentCount : "–"}
          tone="purple"
          valueToned={false}
          icon={<FileText size={19} strokeWidth={1.75} aria-hidden />}
          caption="Generated so far"
        />
        <StatCard
          label="Resumes"
          value={jobs ? jobs.length : "–"}
          tone="orange"
          icon={<Upload size={19} strokeWidth={1.75} aria-hidden />}
          caption={failedCount > 0 ? `${failedCount} failed — open to retry` : "Everything uploaded"}
        />
      </section>

      <div
        role="button"
        tabIndex={0}
        aria-label="Upload a resume"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center",
          "transition-[border-color,background-color,box-shadow] duration-150 ease-out",
          dragOver
            ? "border-brand-500 bg-brand-tint"
            : "border-border-2 bg-surface hover:border-brand-400 hover:shadow-subtle",
          uploading && "pointer-events-none opacity-60",
        )}
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-tint text-brand-on">
          <UploadCloud size={22} strokeWidth={1.75} />
        </span>
        <div>
          <p className="font-semibold text-text">
            {uploading ? "Uploading…" : "Drag a resume here, or click to choose files"}
          </p>
          <p className="mt-0.5 text-[13px] text-text-2">
            PDF, DOCX, DOC, XLSX or TXT · up to 20 MB, 30 pages · choose several files of one
            candidate together to combine them
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={RESUME_ACCEPT}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      <p className="-mt-3 text-center text-[13px] text-text-2">
        No file? You can also{" "}
        <button
          type="button"
          onClick={() => setPasting((v) => !v)}
          className="font-semibold text-brand-on hover:underline"
        >
          paste the candidate&rsquo;s information as text
        </button>
        {" "}— an email or notes work too.
      </p>
      {pasting && (
        <PasteStage
          onConfirm={(text) => void pasteText(text)}
          onCancel={() => setPasting(false)}
          busy={uploading}
        />
      )}
      {error && (
        <p className="rounded-md bg-danger-tint px-4 py-2.5 text-[13px] font-medium text-danger-on">
          {error}
        </p>
      )}
      {pending && (
        <UploadStage
          files={pending}
          onChange={setPending}
          onConfirm={confirmPending}
          onCancel={cancelPending}
          uploading={uploading}
        />
      )}

      {!jobsQuery.isLoading && (jobs?.length ?? 0) === 0 ? (
        <EmptyState
          icon={<Inbox className="h-5 w-5" strokeWidth={1.75} aria-hidden />}
          title="No resumes yet"
          body="Drop the first candidate resume above — extraction usually takes under 30 seconds, then it lands here ready for review."
        />
      ) : (
        <Card>
          <CardHeader className="pb-1">
            <CardTitle>Recent resumes</CardTitle>
            <Link
              to="/resumes"
              className="inline-flex items-center gap-1 text-[13px] font-semibold text-brand-on no-underline hover:underline"
            >
              View all
              <ArrowRight size={14} strokeWidth={2} aria-hidden />
            </Link>
          </CardHeader>
          <CardBody className="px-0 py-0">
            <ResumeTable jobs={recent} loading={jobsQuery.isLoading} onDelete={(id) => void deleteJob(id)} />
          </CardBody>
        </Card>
      )}
    </div>
  );
}
